import { FC, useState, useRef, useEffect } from 'react';
import { Icon, Divider } from 'common-components';
import Tooltip from '../tooltip';
import { useTranslationContext } from '../contexts/translationContext';
import SearchFilters from '../searchFilters/searchFilters';
import { InputAdornment, SearchIcon, TextField, CloseIcon } from '../uiLibraryImports';
import { SearchAutoCompleteTypes } from './searchAutocompleteTypes';
import styles from './searchAutocomplete.module.scss';

const SearchAutocomplete: FC<SearchAutoCompleteTypes> = (props: SearchAutoCompleteTypes) => {
    const {
        options,
        languages,
        types,
        progress,
        searchString,
        setSearchString,
        assignedToMe,
        selectedFilters,
        setAssignedToMe,
        setSelectedFilters,
        triggerMixpanel
    } = props;
    const { translate } = useTranslationContext();
    const [isOpen, setIsOpen] = useState(false);
    const [numberOfFiltersApplied, setNumberOfFiltersApplied] = useState(0);
    const ref = useRef() as React.MutableRefObject<HTMLInputElement>;
    const textFieldRef = useRef() as React.MutableRefObject<HTMLInputElement>;

    useEffect(() => {
        const checkIfClickedOutside = (e: any) => {
            if (isOpen && ref.current && !ref.current.contains(e.target) && !textFieldRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', checkIfClickedOutside);
        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [isOpen]);

    const handleReset = () => {
        setIsOpen(false);
        setSelectedFilters([]);
        setAssignedToMe(false);
        setNumberOfFiltersApplied(0);
        setSearchString('');
    };

    const onFocus = () => {
        triggerMixpanel?.('Search Started', {
            Location: 'Home',
            Filters: selectedFilters.length
        });

        if (searchString === undefined) {
            setSearchString('');
            setIsOpen(true);
        } else {
            !isOpen && setIsOpen(true);
        }
    };

    const onSearch = e => {
        const { value } = e.target;
        setSearchString(value);
        !isOpen && value && setIsOpen(true);
    };

    const iconByType = {
        Lesson: 'lessonIconLearningPanel',
        Course: 'courseIconLearningPanel',
        Challenge: 'challengeIconLearningPanel',
        Video: 'videoLearningPanel',
        'Skill Tree': 'skillTree',
        'Learning Path': 'path'
    };

    return (
        <div className={styles.wrapper}>
            <TextField
                ref={textFieldRef}
                value={searchString}
                autoComplete="off"
                placeholder="Search"
                sx={{
                    width: '20rem',
                    backgroundColor: isOpen ? '#4A3859' : 'FFFFFFB2',
                    borderRadius: '4px',
                    border: '1px solid',
                    borderColor: isOpen ? '#479AEB' : 'rgba(255, 255, 255, 0.06)',
                    fontWeight: 700,
                    '&:hover': {
                        borderColor: isOpen ? '#479AEB' : 'rgba(255, 255, 255, 0.7)'
                    },
                    '&:focus-within, &:focus-visible': {
                        borderColor: '#479AEB'
                    }
                }}
                onFocus={onFocus}
                onChange={e => onSearch(e)}
                InputProps={{
                    sx: {
                        height: '2.2rem'
                    },
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.45)' }} />
                        </InputAdornment>
                    ),
                    ...(searchString !== undefined && {
                        endAdornment: (
                            <InputAdornment position="end">
                                {searchString && (
                                    <CloseIcon className={styles['close-icon']} onClick={() => setSearchString('')} />
                                )}
                                {numberOfFiltersApplied ? (
                                    <div className={styles['filter-icon-wrapper']}>
                                        <div className={styles.selectedFilters}>{numberOfFiltersApplied}</div>
                                        <Icon name="filter" className={styles['filter-icon']} />
                                        <Divider
                                            orientation="vertical"
                                            color="rgba(255, 255, 255, 0.5)"
                                            className={styles.divider}
                                        />
                                        <div style={{ position: 'relative' }}>
                                            <Tooltip
                                                title={translate('ClearFilters')}
                                                maxWidth="11rem"
                                                placement={'right'}
                                            >
                                                <div>
                                                    <Icon
                                                        onClick={() => {
                                                            setIsOpen(true);
                                                            setSelectedFilters([]);
                                                            setAssignedToMe(false);
                                                            setNumberOfFiltersApplied(0);
                                                            triggerMixpanel?.('Search Filter Cleared', {
                                                                Filters: 0,
                                                                Assigned: assignedToMe ? 'On' : 'Off'
                                                            });
                                                        }}
                                                        name="trashLight"
                                                        className={styles['trash-icon']}
                                                    />
                                                </div>
                                            </Tooltip>
                                        </div>
                                    </div>
                                ) : null}
                            </InputAdornment>
                        )
                    })
                }}
            />
            {isOpen && (
                <div ref={ref} className={styles['option-wrapper']}>
                    <div className={styles.filters}>
                        <SearchFilters
                            languages={languages}
                            types={types}
                            progress={progress}
                            selectedFilters={selectedFilters}
                            assignedToMe={assignedToMe}
                            setSelectedFilters={setSelectedFilters}
                            setAssignedToMe={setAssignedToMe}
                            setNumberOfFiltersApplied={setNumberOfFiltersApplied}
                        />
                    </div>

                    {options.length > 0 ? (
                        <div className={styles.options}>
                            {options.map((option, i) => (
                                <a
                                    href={option.contentType !== 'Video' ? option.url : undefined}
                                    className={styles.option}
                                    key={i}
                                    onClick={e => {
                                        if (option.contentType === 'Video') {
                                            option.onClick?.();
                                            handleReset();
                                            e.preventDefault();
                                        }
                                        triggerMixpanel?.('Search Item Selected', {
                                            Filters: selectedFilters?.length,
                                            SelectedType: option?.contentType,
                                            SelectedValue: option?.displayName,
                                            Assigned: assignedToMe
                                        });
                                    }}
                                >
                                    <Icon
                                        key={iconByType[option.contentType]}
                                        name={iconByType[option.contentType]}
                                        className={styles['option-icon']}
                                    />
                                    <div className={styles['option-texts']}>
                                        <div className={styles['option-title']}>{option.displayName} </div>
                                        <div className={styles['option-subtitle']}>{option.description}</div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    ) : (
                        <div className={styles['no-results-wrapper']}>
                            <div className={styles.title}>{translate('NoResults')}</div>
                            <div className={styles.subtitle}>{translate('TryDifferentSearch')}</div>
                            <Icon name="noSearchResults" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchAutocomplete;
